import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit, FiTrash2, FiDownload, FiAward, FiArchive } from 'react-icons/fi';
import DiplomaTemplateEditor from '../components/DiplomaTemplateEditor';
import DiplomaPreview from '../components/DiplomaPreview';
import { useAuth } from '../contexts/AuthContext';
import {
  DEFAULT_DIPLOMA_LAYOUT,
  mergeFieldLayout,
  normalizeParticipantsText,
  formatDiplomaDateDisplay,
  firstParticipantName,
} from '../utils/diplomaLayout';
import './Diplomas.css';

const DEFAULT_LAYOUT = DEFAULT_DIPLOMA_LAYOUT;

const Diplomas = () => {
  const { isModerator } = useAuth();
  const [tab, setTab] = useState('templates');
  const [templates, setTemplates] = useState([]);
  const [issued, setIssued] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [templateFile, setTemplateFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [fieldLayout, setFieldLayout] = useState(DEFAULT_LAYOUT);
  const [fontColor, setFontColor] = useState('#1a1a1a');

  const [generateForm, setGenerateForm] = useState({
    template_id: '',
    speaker_name: '',
    event_date: '',
    topic: '',
    participants: '',
  });
  const [lastBatch, setLastBatch] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');

  const fetchTemplates = async () => {
    const response = await axios.get('/api/diplomas/templates/');
    const list = Array.isArray(response.data) ? response.data : response.data?.results || [];
    setTemplates(list);
    return list;
  };

  useEffect(() => {
    loadAll();
    const onScopeChange = () => {
      fetchTemplates().catch(() => toast.error('Ошибка загрузки шаблонов'));
    };
    window.addEventListener('moderator-company-scope-changed', onScopeChange);
    return () => window.removeEventListener('moderator-company-scope-changed', onScopeChange);
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [templatesRes, issuedRes, speakersRes, companiesRes] = await Promise.all([
        axios.get('/api/diplomas/templates/'),
        axios.get('/api/diplomas/issued/'),
        axios.get('/api/speakers/'),
        axios.get('/api/companies/').catch(() => ({ data: [] })),
      ]);
      setTemplates(Array.isArray(templatesRes.data) ? templatesRes.data : templatesRes.data?.results || []);
      setIssued(Array.isArray(issuedRes.data) ? issuedRes.data : issuedRes.data?.results || []);
      setSpeakers(Array.isArray(speakersRes.data) ? speakersRes.data : speakersRes.data?.results || []);
      const companyList = Array.isArray(companiesRes.data) ? companiesRes.data : companiesRes.data?.results || [];
      setCompanies(companyList);
      if (!companyId && companyList.length === 1) {
        setCompanyId(String(companyList[0].id));
      }
    } catch (error) {
      toast.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const openCreateTemplate = () => {
    setEditingTemplate(null);
    setTemplateName('');
    setTemplateFile(null);
    setPreviewUrl('');
    setFieldLayout(DEFAULT_LAYOUT);
    setFontColor('#1a1a1a');
    if (companies.length === 1) {
      setCompanyId(String(companies[0].id));
    }
    setEditorOpen(true);
  };

  const openEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateName(template.name || '');
    setTemplateFile(null);
    setPreviewUrl(template.background_url || '');
    setFieldLayout(mergeFieldLayout(template.field_layout));
    setFontColor(template.font_color || '#1a1a1a');
    setEditorOpen(true);
  };

  const onTemplateFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setTemplateFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const formatApiError = (error, fallback = 'Ошибка сохранения') => {
    const data = error.response?.data;
    if (!data) return fallback;
    if (typeof data === 'string') return data;
    if (data.error) return data.error;
    if (typeof data === 'object') {
      const first = Object.entries(data).find(([, v]) => v);
      if (first) {
        const [field, msg] = first;
        const text = Array.isArray(msg) ? msg[0] : String(msg);
        return field === 'non_field_errors' ? text : `${field}: ${text}`;
      }
    }
    return fallback;
  };

  const saveTemplate = async () => {
    if (!templateName.trim()) {
      toast.error('Укажите название шаблона');
      return;
    }
    if (!editingTemplate && !templateFile) {
      toast.error('Загрузите изображение JPG или PNG');
      return;
    }
    if (!editingTemplate && companies.length > 1 && !companyId) {
      toast.error('Выберите фирму для шаблона');
      return;
    }
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('name', templateName.trim());
      formData.append('field_layout', JSON.stringify(fieldLayout));
      formData.append('font_color', fontColor);
      if (companyId) {
        formData.append('company_id', companyId);
      }
      if (templateFile) {
        formData.append('background', templateFile, templateFile.name);
      }
      let saved;
      if (editingTemplate) {
        const response = await axios.patch(`/api/diplomas/templates/${editingTemplate.id}/`, formData);
        saved = response.data;
        toast.success('Шаблон обновлён');
      } else {
        const response = await axios.post('/api/diplomas/templates/', formData);
        saved = response.data;
        toast.success('Шаблон сохранён');
      }
      setEditorOpen(false);
      setTab('templates');
      if (saved?.id) {
        setTemplates((prev) => [saved, ...prev.filter((item) => item.id !== saved.id)]);
      }
      await fetchTemplates();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const deleteTemplate = async (template) => {
    if (!window.confirm(`Удалить шаблон «${template.name}»?`)) return;
    try {
      await axios.delete(`/api/diplomas/templates/${template.id}/`);
      toast.success('Шаблон удалён');
      loadAll();
    } catch (error) {
      toast.error('Ошибка удаления');
    }
  };

  const handleSpeakerSelect = (speakerId) => {
    const speaker = speakers.find((s) => String(s.id) === String(speakerId));
    if (speaker) {
      const name = speaker.full_name || `${speaker.last_name || ''} ${speaker.first_name || ''} ${speaker.middle_name || ''}`.trim();
      setGenerateForm((prev) => ({ ...prev, speaker_name: name }));
    }
  };

  const generateDiplomas = async (e) => {
    e.preventDefault();
    if (!generateForm.template_id) {
      toast.error('Выберите шаблон');
      return;
    }
    const participants = normalizeParticipantsText(generateForm.participants);
    if (!participants) {
      toast.error('Укажите хотя бы одного участника');
      return;
    }
    setGenerateForm((prev) => ({ ...prev, participants }));
    setGenerating(true);
    try {
      const response = await axios.post('/api/diplomas/generate/', {
        template_id: Number(generateForm.template_id),
        speaker_name: generateForm.speaker_name,
        event_date: generateForm.event_date,
        topic: generateForm.topic,
        participants,
      });
      const batch = response.data?.batch || null;
      setLastBatch(batch);
      toast.success(`Сформировано дипломов: ${response.data?.count || 0}`);
      setGenerateForm((prev) => ({ ...prev, participants: '' }));
      loadAll();
      if (batch?.id) {
        const selectedTemplate = templates.find((t) => String(t.id) === String(generateForm.template_id));
        await downloadZip(batch.id, {
          ...batch,
          event_date: batch.event_date || generateForm.event_date,
          speaker_name: batch.speaker_name || generateForm.speaker_name,
          topic: batch.topic || generateForm.topic,
          template_name: batch.template_name || selectedTemplate?.name || '',
        });
      }
    } catch (error) {
      const data = error.response?.data;
      toast.error(data?.error || data?.participants?.[0] || 'Ошибка формирования');
    } finally {
      setGenerating(false);
    }
  };

  const formatDiplomaDate = formatDiplomaDateDisplay;

  const selectedGenerateTemplate = useMemo(
    () => templates.find((t) => String(t.id) === String(generateForm.template_id)) || null,
    [templates, generateForm.template_id]
  );

  const previewParticipantName = useMemo(
    () => firstParticipantName(generateForm.participants),
    [generateForm.participants]
  );

  const sanitizeFilenamePart = (value) => {
    const cleaned = String(value || '')
      .replace(/[\\/:*?"<>|\n\r\t]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return cleaned || 'без_названия';
  };

  const diplomaBaseFilename = ({ event_date, speaker_name, topic, template_name } = {}) => {
    const datePart = sanitizeFilenamePart(formatDiplomaDate(event_date));
    const surname = sanitizeFilenamePart((speaker_name || '').trim().split(/\s+/)[0] || 'Спикер');
    const subject = sanitizeFilenamePart((topic || '').trim() || (template_name || '').trim() || 'Диплом');
    return `${datePart}_${surname}_${subject}`;
  };

  const filenameFromContentDisposition = (header) => {
    if (!header) return null;
    const utf = /filename\*=(?:UTF-8''|utf-8'')([^;]+)/i.exec(header);
    if (utf?.[1]) {
      try {
        return decodeURIComponent(utf[1].trim().replace(/^"+|"+$/g, ''));
      } catch {
        return utf[1].trim();
      }
    }
    const plain = /filename="([^"]+)"/i.exec(header) || /filename=([^;]+)/i.exec(header);
    if (plain?.[1]) {
      const name = plain[1].trim();
      if (name === 'diplomas.zip' || name === 'diploma.pdf') return null;
      return name;
    }
    return null;
  };

  const downloadZip = async (batchId, batchMeta = null) => {
    try {
      const response = await axios.get(`/api/diplomas/batches/${batchId}/zip/`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      const meta = batchMeta || lastBatch || {};
      const fromHeader = filenameFromContentDisposition(response.headers['content-disposition']);
      const built = `${diplomaBaseFilename(meta)}.zip`;
      link.download = fromHeader || built;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Не удалось скачать архив');
    }
  };

  const downloadPdf = async (row) => {
    try {
      const response = await axios.get(`/api/diplomas/issued/${row.id}/download/`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      const fromHeader = filenameFromContentDisposition(response.headers['content-disposition']);
      const base = diplomaBaseFilename(row);
      const participant = sanitizeFilenamePart(row.participant_name);
      link.download = fromHeader || `${base}_${participant}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Не удалось скачать PDF');
    }
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  return (
    <div className="diplomas-page">
      <div className="page-header">
        <h1><FiAward /> Дипломы</h1>
      </div>

      <div className="diplomas-tabs">
        <button type="button" className={tab === 'templates' ? 'active' : ''} onClick={() => setTab('templates')}>
          Шаблоны
        </button>
        <button type="button" className={tab === 'generate' ? 'active' : ''} onClick={() => setTab('generate')}>
          Выпуск
        </button>
        <button type="button" className={tab === 'journal' ? 'active' : ''} onClick={() => setTab('journal')}>
          Журнал
        </button>
      </div>

      {tab === 'templates' && (
        <section className="diplomas-section">
          <div className="diplomas-section-header">
            <h2>Шаблоны дипломов {templates.length > 0 && <span className="diplomas-count">({templates.length})</span>}</h2>
            <button type="button" className="btn btn-primary" onClick={openCreateTemplate}>
              <FiPlus /> Новый шаблон
            </button>
          </div>
          {templates.length === 0 ? (
            <div className="diplomas-empty">Шаблонов пока нет. Загрузите JPG/PNG и настройте поля.</div>
          ) : (
            <div className="diplomas-template-grid">
              {templates.map((template) => (
                <article key={template.id} className="diplomas-template-card">
                  <div className="diplomas-template-preview">
                    {template.background_url ? (
                      <img src={template.background_url} alt={template.name} />
                    ) : (
                      <span>Нет превью</span>
                    )}
                  </div>
                  <div className="diplomas-template-body">
                    <h3>{template.name}</h3>
                    {template.company_name && (
                      <div className="diplomas-template-company">{template.company_name}</div>
                    )}
                    <div className="diplomas-template-actions">
                      <button type="button" className="btn btn-outline btn-sm" onClick={() => openEditTemplate(template)}>
                        <FiEdit /> Настроить
                      </button>
                      <button type="button" className="btn btn-outline btn-sm danger" onClick={() => deleteTemplate(template)}>
                        <FiTrash2 />
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      )}

      {tab === 'generate' && (
        <section className="diplomas-section">
          <h2>Формирование дипломов</h2>
          <div className="diplomas-generate-layout">
            <form className="diplomas-generate-form" onSubmit={generateDiplomas}>
            <label className="diplomas-field">
              <span>Шаблон *</span>
              <select
                className="form-select"
                value={generateForm.template_id}
                onChange={(e) => setGenerateForm({ ...generateForm, template_id: e.target.value })}
                required
              >
                <option value="">Выберите шаблон</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </label>

            <label className="diplomas-field">
              <span>Спикер из справочника</span>
              <select className="form-select" defaultValue="" onChange={(e) => handleSpeakerSelect(e.target.value)}>
                <option value="">Выберите или введите вручную</option>
                {speakers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.full_name || `${s.last_name} ${s.first_name}`}
                  </option>
                ))}
              </select>
            </label>

            <label className="diplomas-field">
              <span>Спикер *</span>
              <input
                className="form-input"
                value={generateForm.speaker_name}
                onChange={(e) => setGenerateForm({ ...generateForm, speaker_name: e.target.value })}
                required
              />
            </label>

            <label className="diplomas-field">
              <span>Дата *</span>
              <input
                type="date"
                className="form-input"
                value={generateForm.event_date}
                onChange={(e) => setGenerateForm({ ...generateForm, event_date: e.target.value })}
                required
              />
            </label>

            <label className="diplomas-field">
              <span>Тема (необязательно)</span>
              <input
                className="form-input"
                value={generateForm.topic}
                onChange={(e) => setGenerateForm({ ...generateForm, topic: e.target.value })}
                placeholder="Тема мероприятия"
              />
            </label>

            <label className="diplomas-field">
              <span>ФИО участников * (по одному на строку; запятые тоже разобьются)</span>
              <textarea
                className="form-input diplomas-textarea"
                rows={8}
                value={generateForm.participants}
                onChange={(e) => setGenerateForm({ ...generateForm, participants: e.target.value })}
                onBlur={() =>
                  setGenerateForm((prev) => ({
                    ...prev,
                    participants: normalizeParticipantsText(prev.participants),
                  }))
                }
                placeholder={'Иванова Мария Петровна\nПетров Сергей Иванович'}
                required
              />
            </label>

            <button type="submit" className="btn btn-primary" disabled={generating}>
              {generating ? 'Формирование…' : 'Сформировать и скачать'}
            </button>
            </form>

            <aside className="diplomas-generate-preview">
              <h3>Предпросмотр</h3>
              <DiplomaPreview
                template={selectedGenerateTemplate}
                speakerName={generateForm.speaker_name}
                eventDate={formatDiplomaDateDisplay(generateForm.event_date)}
                topic={generateForm.topic}
                participantName={previewParticipantName}
              />
            </aside>
          </div>

          {lastBatch && (
            <div className="diplomas-last-batch">
              <strong>Последний пакет:</strong> {lastBatch.participants_count} дипломов
              <button type="button" className="btn btn-outline btn-sm" onClick={() => downloadZip(lastBatch.id)}>
                <FiArchive /> Скачать ZIP ещё раз
              </button>
            </div>
          )}
        </section>
      )}

      {tab === 'journal' && (
        <section className="diplomas-section">
          <h2>Журнал выданных дипломов</h2>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Номер</th>
                  <th>Участник</th>
                  <th>Спикер</th>
                  <th>Дата</th>
                  <th>Тема</th>
                  <th>Шаблон</th>
                  <th>Выдан</th>
                  <th>PDF</th>
                </tr>
              </thead>
              <tbody>
                {issued.map((row) => (
                  <tr key={row.id}>
                    <td><code>{row.diploma_number}</code></td>
                    <td>{row.participant_name}</td>
                    <td>{row.speaker_name}</td>
                    <td>{new Date(row.event_date).toLocaleDateString('ru-RU')}</td>
                    <td>{row.topic || '—'}</td>
                    <td>{row.template_name}</td>
                    <td>
                      <div>{new Date(row.issued_at).toLocaleString('ru-RU')}</div>
                      <small>{row.issued_by_email}</small>
                    </td>
                    <td>
                      <button type="button" className="btn btn-outline btn-sm" onClick={() => downloadPdf(row)}>
                        <FiDownload />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {issued.length === 0 && <div className="diplomas-empty">Выданных дипломов пока нет</div>}
          </div>
        </section>
      )}

      {editorOpen && (
        <div className="modal-overlay" onClick={() => setEditorOpen(false)}>
          <div className="modal-content diplomas-editor-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingTemplate ? 'Настройка шаблона' : 'Новый шаблон'}</h2>
              <button type="button" className="modal-close" onClick={() => setEditorOpen(false)}>×</button>
            </div>
            <div className="diplomas-editor-body">
              <div className="diplomas-editor-fields">
                <label className="diplomas-field">
                  <span>Название шаблона *</span>
                  <input className="form-input" value={templateName} onChange={(e) => setTemplateName(e.target.value)} />
                </label>
                {isModerator && companies.length > 1 && !editingTemplate && (
                  <label className="diplomas-field">
                    <span>Фирма *</span>
                    <select
                      className="form-select"
                      value={companyId}
                      onChange={(e) => setCompanyId(e.target.value)}
                      required
                    >
                      <option value="">Выберите фирму</option>
                      {companies.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </label>
                )}
                <label className="diplomas-field">
                  <span>Изображение (JPG/PNG) {!editingTemplate && '*'}</span>
                  <input type="file" accept="image/jpeg,image/png,.jpg,.jpeg,.png" onChange={onTemplateFileChange} />
                </label>
              </div>
              <DiplomaTemplateEditor
                imageUrl={previewUrl}
                layout={fieldLayout}
                fontColor={fontColor}
                onLayoutChange={setFieldLayout}
                onFontColorChange={setFontColor}
              />
              <div className="diplomas-editor-actions">
                <button type="button" className="btn btn-outline" onClick={() => setEditorOpen(false)}>Отмена</button>
                <button type="button" className="btn btn-primary" onClick={saveTemplate} disabled={saving}>
                  {saving ? 'Сохранение…' : 'Сохранить шаблон'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Diplomas;
